import { apiClient } from './api'

export type ReportEntity = 'project' | 'task' | 'time_entry' | 'program' | 'portfolio'
export type ReportFormat = 'csv' | 'xlsx' | 'pdf' | 'pptx' | 'json'
export type FilterOp = 'eq' | 'neq' | 'in' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'between'

export interface FilterClause {
  field: string
  op: FilterOp
  value: unknown
}

export interface SavedReport {
  id: number
  name: string
  description?: string | null
  entity_type: ReportEntity
  filters: FilterClause[]
  group_by: string[]
  columns: string[]
  created_at: string
}

export interface ReportMeta {
  entities: Record<ReportEntity, string[]>
  ops: FilterOp[]
  formats: ReportFormat[]
}

export const savedReportsService = {
  meta: async () => {
    const { data } = await apiClient.get<ReportMeta>('/api/v1/saved-reports/_meta')
    return data
  },

  list: async () => {
    const { data } = await apiClient.get<SavedReport[]>('/api/v1/saved-reports')
    return data
  },

  create: async (body: Omit<SavedReport, 'id' | 'created_at'>) => {
    const { data } = await apiClient.post<SavedReport>('/api/v1/saved-reports', body)
    return data
  },

  update: async (id: number, body: Partial<Omit<SavedReport, 'id' | 'created_at' | 'entity_type'>>) => {
    const { data } = await apiClient.put<SavedReport>(`/api/v1/saved-reports/${id}`, body)
    return data
  },

  remove: async (id: number) => {
    await apiClient.delete(`/api/v1/saved-reports/${id}`)
  },

  run: async (id: number) => {
    const { data } = await apiClient.get<{ columns: string[]; rows: Record<string, unknown>[]; row_count: number }>(
      `/api/v1/saved-reports/${id}/run`,
    )
    return data
  },

  exportUrl: (id: number, format: ReportFormat) =>
    `/api/v1/saved-reports/${id}/export?format=${format}`,

  download: async (id: number, format: ReportFormat, name: string) => {
    const res = await apiClient.get(`/api/v1/saved-reports/${id}/export`, {
      params: { format }, responseType: 'blob',
    })
    const blob = res.data as Blob
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${name.toLowerCase().replace(/\s+/g, '_')}.${format}`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  },
}
